import { Certame } from "../@types";
import Service from "./Service";

class CertameService extends Service {
  static getCertames() {
    return this.Http.get<Certame.Certame[]>("/selectionprocesses").then(
      this.getData,
    );
  }

  // Backend prerequisite (certame-admin-redesign-api, see design.md): this route
  // does not exist yet in `back/cmd/internal/database/selectionprocesses.go` —
  // only list/create/update/delete/+disabled/+questions are wired today. The
  // editor page is built against the contract so both repos ship against the
  // same shape; `GET /certame/:id` will 404 until that task lands.
  static getById(id: string) {
    return this.Http.get<Certame.Certame>(`/selectionprocesses/${id}`).then(
      this.getData,
    );
  }

  static create(certame: Certame.Certame) {
    return this.Http.post<Certame.Certame>("/selectionprocesses", certame).then(
      this.getData,
    );
  }

  static update(certame: Certame.Certame) {
    return this.Http.put<Certame.Certame>(
      `/selectionprocesses/${certame.id}`,
      certame,
    ).then(this.getData);
  }

  static delete(certame: Certame.Certame) {
    return this.Http.delete<Certame.Certame>(
      `/selectionprocesses/${certame.id}`,
    ).then(this.getData);
  }

  static setDisabled(id: string, disabled: boolean) {
    return this.Http.put(
      `/selectionprocesses/${id}/disabled`,
      { disabled },
      { headers: { "Content-Type": "application/json" } },
    ).then(this.getData);
  }

  static getQuestionCount(id: string) {
    return this.Http
      .get<{ total: number }>(`/selectionprocesses/${id}/questions/count`)
      .then(this.getData);
  }

  static getQuestionTagCounts(id: string) {
    return this.Http
      .get<Certame.TagCount[]>(`/selectionprocesses/${id}/questions/tagcounts`)
      .then(this.getData);
  }

  static getCatalog() {
    return this.Http.get<Certame.CatalogRow[]>("/selectionprocesses").then(
      this.getData,
    );
  }

  static duplicate(id: string) {
    return this.Http.post<Certame.Certame>(`/selectionprocesses/${id}/duplicate`).then(
      this.getData,
    );
  }

  /**
   * O painel de tags do certame, traduzido do dialeto do Go.
   *
   * As duas pontas foram escritas contra o mesmo design.md e divergiram. O Go
   * (`TagPanelResponse`, `cmd/api/selectionprocess_tags.go:360`) devolve
   * `subjects`, e cada tag como `{tag, questionCount, hidden, needsReview}`; as
   * telas leem `bySubject` e `{value, count, visible, needsReview}`. A diferença
   * mais perigosa é `hidden` × `visible`: **invertida**, não só renomeada —
   * corrigir só o nome deixaria todo chip do painel com o estado trocado, sem
   * erro nenhum.
   *
   * `panel.bySubject` vir `undefined` é o que produzia
   * "Cannot read properties of undefined (reading 'forEach')" no carregamento da
   * página. Normalizar aqui, e não na tela, é a regra do CLAUDE.md: contrato que
   * diverge se conserta nos tipos e no serviço.
   */
  static getTagPanel(id: string): Promise<Certame.TagPanel> {
    return this.Http.get<any>(`/selectionprocesses/${id}/tags`)
      .then(this.getData)
      .then((raw: any) => CertameService.normalizeTagPanel(raw));
  }

  /** Aceita os dois dialetos: o do Go hoje, e o canônico se o back convergir. */
  static normalizeTagPanel(raw: any): Certame.TagPanel {
    const grupos: any[] = raw?.bySubject ?? raw?.subjects ?? [];

    const bySubject: Certame.SubjectTags[] = grupos.map((g: any) => {
      const tags: Certame.TagEntry[] = (g?.tags ?? []).map((t: any) => ({
        value: String(t?.value ?? t?.tag ?? ""),
        count: Number(t?.count ?? t?.questionCount ?? 0),
        // `hidden` é o campo do Go e é o oposto de `visible`. O `??` encadeado
        // só cai no `hidden` quando `visible` não vem — nunca os soma.
        visible:
          typeof t?.visible === "boolean" ? t.visible : !(t?.hidden ?? false),
        needsReview: Boolean(t?.needsReview),
      }));

      return {
        subjectId: String(g?.subjectId ?? ""),
        subjectName: String(g?.subjectName ?? ""),
        // Derivados: o Go não manda nenhum dos dois.
        totalTags: Number(g?.totalTags ?? tags.length),
        visibleTags: Number(
          g?.visibleTags ?? tags.filter((t) => t.visible).length,
        ),
        tags,
      };
    });

    return {
      totalUnique: Number(raw?.totalUnique ?? 0),
      visibleCount: Number(raw?.visibleCount ?? 0),
      hiddenCount: Number(raw?.hiddenCount ?? 0),
      needsReviewCount: Number(raw?.needsReviewCount ?? 0),
      bySubject,
    };
  }

  /**
   * Visibilidade das tags, traduzida para o corpo que o Go aceita.
   *
   * O Go espera um **array** de `{subjectId, tag, hidden}`
   * (`tagVisibilityItem`, `selectionprocess_tags.go:250`), um item por tag, e
   * recusa lista vazia com 400. As telas falam em `{subjectId, tags, visible}`.
   * Sem esta tradução a requisição ia como objeto e tomava
   * "JSON inválido" — e `hidden` é o inverso de `visible`, então um ajuste só de
   * nome inverteria a escrita.
   */
  static setTagVisibility(id: string, body: Certame.SetTagVisibilityRequest) {
    const items = body.tags.map((tag) => ({
      subjectId: body.subjectId,
      tag,
      hidden: !body.visible,
    }));
    return this.Http.put(`/selectionprocesses/${id}/tags/visibility`, items).then(
      this.getData,
    );
  }

  static getDuplicateTagGroups(id: string) {
    return this.Http
      .get<Certame.DuplicateTagGroups>(`/selectionprocesses/${id}/tags/duplicates`)
      .then(this.getData);
  }

  static mergeTagGroup(id: string, body: Certame.MergeTagGroupRequest) {
    return this.Http.post(`/selectionprocesses/${id}/tags/merge`, body).then(
      this.getData,
    );
  }

  static ignoreTagGroup(id: string, body: Certame.IgnoreTagGroupRequest) {
    return this.Http.post(`/selectionprocesses/${id}/tags/duplicates/ignore`, body).then(
      this.getData,
    );
  }

  static mergeAllTagGroups(id: string) {
    return this.Http
      .post<Certame.MergeAllTagGroupsResponse>(`/selectionprocesses/${id}/tags/merge-all`)
      .then(this.getData);
  }

  static getCommentSnippetTags(id: string) {
    return this.Http
      .get<Certame.CommentSnippetTags>(`/selectionprocesses/${id}/tags/comment-snippets`)
      .then(this.getData);
  }

  static deleteTag(id: string, tag: string) {
    return this.Http.delete(`/selectionprocesses/${id}/tags/${encodeURIComponent(tag)}`).then(
      this.getData,
    );
  }

  static deleteAllCommentSnippets(id: string) {
    return this.Http.post(`/selectionprocesses/${id}/tags/comment-snippets/delete-all`).then(
      this.getData,
    );
  }
}

export default CertameService;
